<script setup>
import { computed, onBeforeUnmount, ref, watch } from 'vue'
import NavTooltip from './nav-tooltip'

const HOVER_INTENT_MS = 300

const props = defineProps({
    destination: {
        type: Object,
        required: true,
    },
    // expanded = icon + label · rail = icon + tooltip (desktop) · tablet = icon over label
    layout: {
        type: String,
        required: true,
        validator: value => ['expanded', 'rail', 'tablet'].includes(value),
    },
    selected: {
        type: Boolean,
        default: false,
    },
    premium: {
        type: Boolean,
        default: false,
    },
    tooltipsSuppressed: {
        type: Boolean,
        default: false,
    },
})

const emit = defineEmits(['click'])

const isTooltipVisible = ref(false)
let intentTimer = null

const isRail = computed(() => props.layout === 'rail')
const label = computed(() =>
    props.layout === 'tablet' && props.destination.shortLabel ? props.destination.shortLabel : props.destination.label
)

function showTooltipSoon() {
    if (!isRail.value || props.tooltipsSuppressed) return
    clearTimeout(intentTimer)
    intentTimer = setTimeout(() => {
        isTooltipVisible.value = !props.tooltipsSuppressed
    }, HOVER_INTENT_MS)
}

function hideTooltip() {
    clearTimeout(intentTimer)
    isTooltipVisible.value = false
}

watch(() => props.tooltipsSuppressed, suppressed => {
    if (suppressed) hideTooltip()
})
watch(() => props.layout, hideTooltip)
onBeforeUnmount(() => clearTimeout(intentTimer))
</script>

<template>
    <router-link
        :to="destination.to"
        :class="[
            'nav-item',
            `nav-item-${layout}`,
            { 'nav-item-selected': selected, 'nav-item-premium': premium },
        ]"
        :aria-current="selected ? 'page' : undefined"
        :aria-label="isRail ? destination.label : undefined"
        @click.capture="emit('click', $event)"
        @mouseenter="showTooltipSoon"
        @mouseleave="hideTooltip"
        @focus="showTooltipSoon"
        @blur="hideTooltip"
        @keydown.esc="hideTooltip"
    >
        <BaseIcon
            class="nav-item-icon"
            :name="destination.icon"
            :filled="selected && !premium"
            :size="24"
        />
        <span
            v-if="!isRail"
            class="nav-item-label"
        >{{ label }}</span>
        <NavTooltip
            v-if="isRail"
            :text="destination.label"
            :visible="isTooltipVisible"
            aria-hidden="true"
        />
    </router-link>
</template>

<style lang="scss" scoped>
@use '@design' as *;

$rail-chip: 56px;
$rail-icon: 26px;
// Nav glyphs are 1.25 (amends T4's 1.5 — design review 2026-09-09). The sprite pins
// strokes with vector-effect=non-scaling-stroke and carries no stroke-width attributes,
// so this class beats BaseIcon's :where() default at any rendered size.
$nav-stroke: 1.25;
$premium-stroke: 1.5;
$premium-halo: $c-border-strong; // no FDS glow token — the approved T5 exception borrows border-strong's white 40%

.nav-item {
    position: relative;
    display: flex;
    align-items: center;
    text-decoration: none;
    border-radius: $radius-16;

    @include transition((color, background-color));
    @include focusRing;

    &,
    &:visited {
        color: $c-text-tertiary;
    }

    &:hover,
    &:visited:hover {
        color: $c-text-primary;
        text-decoration: none;
        background: $c-state-hover;
    }

    &.nav-item-selected {
        color: $c-text-primary;
        background: $c-state-selected;

        &:hover {
            background: $c-state-pressed;
        }
    }
}

.nav-item-icon {
    flex-shrink: 0;
    stroke-width: $nav-stroke;
}

// Expanded: 48 rows, 12 side padding, icon+label gap 12, Body 16; icon x is 36 in both states.
.nav-item-expanded {
    gap: $space-12;
    height: $size-nav-row;
    padding: 0 $space-12;

    .nav-item-label {
        @include body;

        color: inherit;
        white-space: nowrap;
    }
}

// Rail: 56 chip, 26 glyph, icons only.
.nav-item-rail {
    justify-content: center;
    width: $rail-chip;
    height: $rail-chip;
    margin: 0 auto;

    .nav-item-icon {
        @include square($rail-icon);
    }
}

// Tablet: icon over a Caption/Regular label. Fixed (not min-) width, so every chip shares
// one centre line and no caption can push its icon off it. A longer caption ("Premium" is
// 58.6px against the 56px chip) deliberately overhangs the chip instead of widening it —
// it stays centred and inside the 72px rail (Faisal, 2026-09-16).
.nav-item-tablet {
    flex-direction: column;
    gap: $space-4;
    justify-content: center;
    width: $rail-chip;
    padding: $space-8 $space-4;
    margin: 0 auto;

    .nav-item-icon {
        @include square($rail-icon);
    }

    .nav-item-label {
        @include captionRegular;

        color: inherit;
        white-space: nowrap;
    }
}

// Premium row: accent mark always; hover = lift + soft white halo on top of the wash.
.nav-item-premium {
    .nav-item-icon {
        color: $c-accent-default;
        stroke-width: $premium-stroke;

        @include transition((transform, filter));
    }

    &:hover .nav-item-icon {
        filter: drop-shadow(0 0 4px $premium-halo);
        transform: scale(1.14);
    }
}

@media (prefers-reduced-motion: reduce) {
    .nav-item-premium:hover .nav-item-icon {
        filter: none;
        transform: none;
    }
}
</style>
